import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { DateTime } from 'luxon';
import Table from './Table';
import formatDate from '../helpers/formatDate';

const SENDGRID_DOMAIN_STATUSES = ['unconfigured', 'pending', 'partially_verified', 'verified', 'failed'];
const DOMAIN_STATUS_SORT_ORDER = ['verified', 'partially_verified', 'pending', 'failed', 'unconfigured'];

// Outline badge with a soft tint of the same color.
const BADGE_TONES = {
  success: { color: '#15803d', borderColor: '#4ade80', backgroundColor: 'rgba(34, 197, 94, 0.1)' },
  warning: { color: '#b45309', borderColor: '#fbbf24', backgroundColor: 'rgba(251, 191, 36, 0.12)' },
  info: { color: '#0369a1', borderColor: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.12)' },
  error: { color: '#b91c1c', borderColor: '#f87171', backgroundColor: 'rgba(248, 113, 113, 0.12)' },
  neutral: { color: '#4b5563', borderColor: '#d1d5db', backgroundColor: 'rgba(156, 163, 175, 0.12)' },
};

const DOMAIN_STATUS_TONES = {
  verified: 'success',
  partially_verified: 'warning',
  pending: 'info',
  failed: 'error',
};

function Badge({ tone, title, children }) {
  return (
    <span
      title={title}
      style={{
        ...BADGE_TONES[tone] || BADGE_TONES.neutral,
        display: 'inline-block',
        border: '1px solid',
        borderRadius: '6px',
        padding: '1px 8px',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function DomainStatusBadge({ status }) {
  return <Badge tone={DOMAIN_STATUS_TONES[status]}>{(status || 'unconfigured').replace(/_/g, ' ')}</Badge>;
}

const EMPTY_SYNC_PREVIEW = {
  isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, error: null,
};

export default function DivisionsTable() {
  // HubSpot account (token id) the divisions were loaded for — sent when creating one so
  // the server can refuse if the user has since flipped the FastLane/CarPool switch
  // (see DivisionsController#ensure_same_hubspot_account).
  const loadedHubspotTokenIdRef = useRef(null);
  const [syncPreview, setSyncPreview] = useState(EMPTY_SYNC_PREVIEW);
  const [ffData, setFFData] = useState([]);
  const [loadingFFData, setLoadingFFData] = useState(false);
  const [hubspotData, setHubspotData] = useState([]);
  const [loadingHubspotData, setLoadingHubspotData] = useState(false);

  useEffect(() => {
    setLoadingFFData(true)
    async function fetchData() {
      const response = await fetch("api/divisions");
      const res = await response.json();
      return (res)
    }
    // call the function
    fetchData()
      .then((res) => {
        setFFData(res.data)
        loadedHubspotTokenIdRef.current = res.meta?.hubspot_token_id ?? null;
        setLoadingFFData(false)
      })
      // make sure to catch any error
      .catch(console.error);
  }, []);

  useEffect(() => {
    setLoadingHubspotData(true)
    async function fetchData() {
      const response = await fetch("api/divisions/hubspot");
      const res = await response.json();
      return (res)
    }
    // call the function
    fetchData()
      .then((res) => {
        setHubspotData(res.data)
        setLoadingHubspotData(false)
      })
      // make sure to catch any error
      .catch(console.error);
  }, []);

  const pendingKeysRef = useRef(new Set());
  const [pendingKeys, setPendingKeys] = useState(new Set());
  const [rowErrors, setRowErrors] = useState({});
  const [confirmState, setConfirmState] = useState(null);
  const [addDomainOpenIds, setAddDomainOpenIds] = useState(new Set());
  const [addDomainDrafts, setAddDomainDrafts] = useState({});
  const [domainDetailsModal, setDomainDetailsModal] = useState(null);
  // Division id → draft Sync Key, for rows whose Sync Key is being edited.
  const [syncKeyDrafts, setSyncKeyDrafts] = useState({});
  const tableInstanceRef = useRef(null);

  // Toggles the Sync Status column filter — clicking an already-active status
  // clears it. Reads the current value at click time via the table instance
  // captured from renderToolbar, so it doesn't need filter state lifted into
  // this component just to stay in sync.
  const toggleSyncStatusFilter = React.useCallback((status) => {
    const column = tableInstanceRef.current?.getColumn('sync_status');
    if (!column) return;

    column.setFilterValue((prev) => (prev === status ? undefined : status));
  }, []);

  const closeConfirmation = () => setConfirmState(null);

  const requestConfirmation = (title, message, onConfirm) => {
    setConfirmState({ title, message, onConfirm });
  };

  // Returns true when the update saved; failures are recorded in rowErrors.
  const toggleDivisionField = async (record, field, newValue) => {
    const id = record.ff_record.id;
    const key = `${id}-${field}`;
    if (pendingKeysRef.current.has(key)) return false;

    pendingKeysRef.current.add(key);
    setPendingKeys(new Set(pendingKeysRef.current));
    setRowErrors((prev) => ({ ...prev, [key]: null }));

    try {
      const response = await fetch(`api/divisions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({
          division: { [field]: newValue },
        }),
      });

      const body = await response.json().catch(() => null);

      if (response.ok && body?.data) {
        setFFData((prev) => prev.map((r) => (r.id === id ? body.data : r)));
        return true;
      }

      const message = body?.errors
        ? Object.values(body.errors).flat().join(', ')
        : 'Something went wrong. Please try again.';
      setRowErrors((prev) => ({ ...prev, [key]: message }));
      return false;
    } catch (error) {
      setRowErrors((prev) => ({ ...prev, [key]: 'Network error. Please try again.' }));
      return false;
    } finally {
      pendingKeysRef.current.delete(key);
      setPendingKeys(new Set(pendingKeysRef.current));
    }
  }

  const toggleDisabledAt = (record) => {
    const isCurrentlyDisabled = !!record.ff_record.disabled_at;
    const newValue = isCurrentlyDisabled ? null : DateTime.now().toISO();
    const title = isCurrentlyDisabled ? 'Enable Brand' : 'Disable Brand';
    const message = isCurrentlyDisabled
      ? 'This will resume importing lead data from Hubspot for this brand. Are you sure?'
      : 'This will stop importing lead data from Hubspot for this brand. Are you sure?';
    requestConfirmation(title, message, () => toggleDivisionField(record, 'disabled_at', newValue));
  }

  const postToDivisions = (path, hubspotName) => fetch(`api/divisions/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
    },
    body: JSON.stringify({ hubspot_name: hubspotName, hubspot_token_id: loadedHubspotTokenIdRef.current }),
  });

  // hs_only rows: preview every value the new division would get before creating it
  // (DivisionsController#sync_preview), mirroring the Brokers/Lead Owners sync previews.
  const openSyncPreview = async (record) => {
    setSyncPreview({ ...EMPTY_SYNC_PREVIEW, isOpen: true, isLoading: true, record });

    try {
      const response = await postToDivisions('sync_preview', record.hs_record.value);
      const body = await response.json().catch(() => null);

      if (!response.ok) {
        const message = body?.errors?.join('\n') || `Failed to load preview (HTTP ${response.status})`;
        setSyncPreview((prev) => ({ ...prev, isLoading: false, error: message }));
        return;
      }

      setSyncPreview((prev) => ({ ...prev, isLoading: false, fields: body?.fields || [] }));
    } catch (error) {
      console.error('Division sync preview error:', error);
      setSyncPreview((prev) => ({ ...prev, isLoading: false, error: 'An unexpected error occurred while loading the preview.' }));
    }
  };

  const closeSyncPreview = () => {
    setSyncPreview((prev) => (prev.isSaving ? prev : EMPTY_SYNC_PREVIEW));
  };

  const confirmSyncPreview = async () => {
    const record = syncPreview.record;
    if (!record) return;

    setSyncPreview((prev) => ({ ...prev, isSaving: true, error: null }));

    try {
      const response = await postToDivisions('create_from_hubspot', record.hs_record.value);
      const body = await response.json().catch(() => null);

      if (response.ok && body?.data) {
        setFFData((prev) => [...prev, body.data]);
        setSyncPreview(EMPTY_SYNC_PREVIEW);
        return;
      }

      const message = body?.errors?.join('\n') || `Failed to create division (HTTP ${response.status})`;
      setSyncPreview((prev) => ({ ...prev, isSaving: false, error: message }));
    } catch (error) {
      console.error('Division create error:', error);
      setSyncPreview((prev) => ({ ...prev, isSaving: false, error: 'An unexpected error occurred while creating the division.' }));
    }
  };

  const humanizeDiffKey = (key) => key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatDiffValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">—</span>;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const startSyncKeyEdit = (record) => {
    setSyncKeyDrafts((prev) => ({ ...prev, [record.ff_record.id]: record.ff_record.hubspot_name || '' }));
  };

  const cancelSyncKeyEdit = (divisionId) => {
    setSyncKeyDrafts((prev) => {
      const next = { ...prev };
      delete next[divisionId];
      return next;
    });
    setRowErrors((prev) => ({ ...prev, [`${divisionId}-hubspot_name`]: null }));
  };

  // The Sync Key (hubspot_name) is what HubSpot lead imports match a deal's brand
  // against, so changing it changes which HubSpot brand's leads land in this division.
  const saveSyncKey = (record) => {
    const divisionId = record.ff_record.id;
    const newValue = (syncKeyDrafts[divisionId] || '').trim();
    const currentValue = record.ff_record.hubspot_name || '';
    if (newValue === currentValue) {
      cancelSyncKeyEdit(divisionId);
      return;
    }

    const matchesHubspot = hubspotData.some((hs) => hs.value === newValue);
    const message = [
      `Change the Sync Key for ${record.ff_record.name} from "${currentValue || '(none)'}" to "${newValue || '(none)'}"?`,
      'HubSpot leads are matched to this brand by its Sync Key, so new lead imports for the old key will no longer land in this brand.',
      newValue && !matchesHubspot ? `Warning: "${newValue}" doesn't match any HubSpot brand value, so this brand will show as ff_only.` : null,
      !newValue ? 'Leaving it blank unlinks this brand from HubSpot.' : null,
    ].filter(Boolean).join('\n\n');

    requestConfirmation('Change Sync Key', message, async () => {
      // On failure the editor stays open with the error shown under it.
      if (await toggleDivisionField(record, 'hubspot_name', newValue || null)) {
        cancelSyncKeyEdit(divisionId);
      }
    });
  };

  const toggleBrandVisibility = (record) => {
    const isCurrentlyVisible = !!record.ff_record.carpool_active;
    const newValue = !isCurrentlyVisible;
    const title = isCurrentlyVisible ? 'Hide Brand' : 'Show Brand';
    const message = 'This will change the visibility of this brand. Are you sure?';
    requestConfirmation(title, message, () => toggleDivisionField(record, 'carpool_active', newValue));
  }

  const runSendgridDomainAction = async (key, url, method, body) => {
    if (pendingKeysRef.current.has(key)) return;

    pendingKeysRef.current.add(key);
    setPendingKeys(new Set(pendingKeysRef.current));
    setRowErrors((prev) => ({ ...prev, [key]: null }));

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const responseBody = await response.json().catch(() => null);

      if (responseBody?.data) {
        setFFData((prev) => prev.map((r) => (r.id === responseBody.data.id ? responseBody.data : r)));
      }

      if (!response.ok) {
        const message = responseBody?.message
          || (responseBody?.errors ? Object.values(responseBody.errors).flat().join(', ') : null)
          || 'Something went wrong. Please try again.';
        setRowErrors((prev) => ({ ...prev, [key]: message }));
        return false;
      }

      return true;
    } catch (error) {
      setRowErrors((prev) => ({ ...prev, [key]: 'Network error. Please try again.' }));
      return false;
    } finally {
      pendingKeysRef.current.delete(key);
      setPendingKeys(new Set(pendingKeysRef.current));
    }
  }

  const addSendgridDomain = async (record) => {
    const id = record.ff_record.id;
    const domain = (addDomainDrafts[id] || '').trim();
    if (!domain) return;

    const key = `${id}-add_domain`;
    const success = await runSendgridDomainAction(key, `api/divisions/${id}/sendgrid_domains`, 'POST', { domain });
    if (success) {
      setAddDomainDrafts((prev) => ({ ...prev, [id]: '' }));
      setAddDomainOpenIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  const [verifyAllDomainsState, setVerifyAllDomainsState] = useState({ isRunning: false, message: null, isError: false });

  const verifyAllDomains = async () => {
    setVerifyAllDomainsState({ isRunning: true, message: null, isError: false });

    try {
      const response = await fetch('api/divisions/sendgrid_domains/recheck_all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
      });
      const responseBody = await response.json().catch(() => null);

      // Some domains can fail to check while the rest update, so apply whatever came back either way.
      if (responseBody?.data) setFFData(responseBody.data);

      setVerifyAllDomainsState({
        isRunning: false,
        message: responseBody?.message || 'Something went wrong. Please try again.',
        isError: !response.ok,
      });
    } catch (error) {
      setVerifyAllDomainsState({ isRunning: false, message: 'Network error. Please try again.', isError: true });
    }
  }

  const recheckSendgridDomain = (record, sendgridDomainId) => {
    const id = record.ff_record.id;
    const key = `${id}-recheck-${sendgridDomainId}`;
    runSendgridDomainAction(key, `api/divisions/${id}/sendgrid_domains/${sendgridDomainId}/recheck`, 'POST');
  }

  const removeSendgridDomain = (record, sendgridDomain, onSuccess) => {
    const id = record.ff_record.id;
    const key = `${id}-remove-${sendgridDomain.id}`;
    const title = 'Remove Sending Domain';
    const message = `Stop tracking ${sendgridDomain.domain} for ${record.ff_record.name}? This does not remove the authentication from SendGrid itself.`;
    requestConfirmation(title, message, async () => {
      const success = await runSendgridDomainAction(key, `api/divisions/${id}/sendgrid_domains/${sendgridDomain.id}`, 'DELETE');
      if (success && onSuccess) onSuccess();
    });
  }

  const copyAllDnsRecords = (sendgridDomain) => {
    const dnsRecords = sendgridDomain.dns_records || {};
    const text = Object.values(dnsRecords)
      .map((dnsRecord) => `Type: CNAME\nHost/Name: ${dnsRecord.host}\nValue/Points to: ${dnsRecord.data}`)
      .join('\n\n');
    navigator.clipboard?.writeText(text);
  }

  const calculateSyncStatus = (ff_record, hs_record) => {
    if (!hs_record  && ff_record) return 'ff_only';
    if (hs_record && ff_record && ff_record.disabled_at) return 'ff_disabled_synced';
    return 'synced';
  }

  const ffDataWithHsRecord = ffData.map((ff_record) => {
    const hs_record = hubspotData.find((hs) => hs.value === ff_record.hubspot_name);
    return {
      ff_record,
      hs_record,
      sync_status: calculateSyncStatus(ff_record, hs_record),
    }
  });

  const matchingHsIds = ffData.map((r) => r.hubspot_name);
  const unmatchedHubspotData = hubspotData.filter((hs) => !matchingHsIds.includes(hs.value));
  const unmatchedRecords = unmatchedHubspotData.map((hs) => ({
    ff_record: null,
    hs_record: hs,
    sync_status: 'hs_only',
  }));
  const combinedData = [...ffDataWithHsRecord, ...unmatchedRecords];
  const statusCounts = React.useMemo(() => {
    return combinedData.reduce((acc, row) => {
      acc[row.sync_status] = (acc[row.sync_status] || 0) + 1;
      return acc;
    }, {});
  }, [combinedData]);

  const statusOrder = {
    'synced': 1,
    'ff_disabled_synced': 2,
    'ff_only': 3,
    'hs_only': 4,
  };

  const customSortingFn = (rowA, rowB, columnId) => {
    const valueA = statusOrder[rowA.getValue(columnId)] || 99;
    const valueB = statusOrder[rowB.getValue(columnId)] || 99;
    return valueA - valueB;
  };

  const columns = React.useMemo(
    () => [
      {
        accessorFn: (row) => row.ff_record?.id?.toString() || '',
        header: 'FF ID',
        cell: (info) => {
          const record = info.row.original;
          if (record.ff_record || !record.hs_record) return info.getValue();

          return (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => openSyncPreview(record)}
              disabled={syncPreview.isOpen}
              title="Create this brand as a division in Franchise FastLane"
            >
              Sync
            </button>
          );
        },
      },
      {
        id: 'brand_name',
        accessorFn: (row) => row.ff_record?.name || row.hs_record?.value || '',
        header: 'Brand Name',
      },
      {
        accessorFn: (row) => row.ff_record?.hubspot_name || row.hs_record?.value || '',
        header: 'Sync Key',
        cell: (info) => {
          const record = info.row.original;
          const value = info.getValue();
          if (!record.ff_record) return value;

          const divisionId = record.ff_record.id;
          const syncKeyKey = `${divisionId}-hubspot_name`;
          const isSaving = pendingKeys.has(syncKeyKey);
          const isEditing = Object.prototype.hasOwnProperty.call(syncKeyDrafts, divisionId);

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isEditing ? '260px' : undefined }}>
              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    list="divisions-hubspot-values"
                    value={syncKeyDrafts[divisionId]}
                    disabled={isSaving}
                    autoFocus
                    onChange={(event) => setSyncKeyDrafts((prev) => ({ ...prev, [divisionId]: event.target.value }))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') saveSyncKey(record);
                      if (event.key === 'Escape') cancelSyncKeyEdit(divisionId);
                    }}
                  />
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => saveSyncKey(record)} disabled={isSaving}>
                    {isSaving ? '...' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => cancelSyncKeyEdit(divisionId)} disabled={isSaving}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                  <span>{record.ff_record.hubspot_name || <span className="text-muted">—</span>}</span>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => startSyncKeyEdit(record)}>
                    Edit
                  </button>
                </div>
              )}
              {rowErrors[syncKeyKey] && <div className="text-danger" style={{ fontSize: '11px' }}>{rowErrors[syncKeyKey]}</div>}
            </div>
          );
        },
      },
      {
        accessorKey: 'sync_status',
        header: 'Sync Status',
        meta: { filterVariant: 'select'},
        sortingFn: customSortingFn,
      },
      {
        accessorFn: (row) => row.hs_record?.hidden ? 'Hidden' : 'Visible',
        header: 'HS Hidden',
        meta: { filterVariant: 'select'},
      },
      {
        accessorFn: (row) => !row.ff_record?.carpool_active ? 'Hidden' : 'Visible',
        header: 'FF Hidden(carpool_active)',
        meta: { filterVariant: 'select'},
        cell: (info) => {
          const record = info.row.original;
          const value = info.getValue();
          if (!record.ff_record) return value;

          const visibilityKey = `${record.ff_record.id}-carpool_active`;
          const isTogglingVisibility = pendingKeys.has(visibilityKey);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>{value}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => toggleBrandVisibility(record)}
                  disabled={isTogglingVisibility}
                >
                  {isTogglingVisibility ? '...' : 'Toggle'}
                </button>
              </div>
              {rowErrors[visibilityKey] && <div className="text-danger" style={{ fontSize: '11px' }}>{rowErrors[visibilityKey]}</div>}
            </div>
          );
        },
      },
      {
        accessorFn: (row) => row.ff_record?.disabled_at ? 'Disabled' : 'Active',
        header: 'FF Disabled(disabled_at)',
        meta: { filterVariant: 'select'},
        cell: (info) => {
          const record = info.row.original;
          const value = info.getValue();
          if (!record.ff_record) return value;

          const disabledKey = `${record.ff_record.id}-disabled_at`;
          const isTogglingDisabledAt = pendingKeys.has(disabledKey);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>{value}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => toggleDisabledAt(record)}
                  disabled={isTogglingDisabledAt}
                >
                  {isTogglingDisabledAt ? '...' : 'Toggle'}
                </button>
              </div>
              {rowErrors[disabledKey] && <div className="text-danger" style={{ fontSize: '11px' }}>{rowErrors[disabledKey]}</div>}
            </div>
          );
        },
      },
      {
        accessorKey: 'x',
        header: 'Last Fetched Leads At',
      },
      {
        accessorKey: 'y',
        header: 'Last Fetched Engagements At',
      },
      {
        id: 'sendgrid_domain_status',
        header: 'Domain Status',
        accessorFn: (row) => (row.ff_record?.sendgrid_domains || []).map((sd) => sd.status),
        filterFn: (row, columnId, filterValue) => (
          !filterValue || (row.getValue(columnId) || []).includes(filterValue)
        ),
        // Order by each brand's best domain status; brands without a domain sort last.
        sortingFn: (rowA, rowB, columnId) => {
          const bestRank = (row) => Math.min(
            ...(row.getValue(columnId) || []).map((status) => DOMAIN_STATUS_SORT_ORDER.indexOf(status)),
            DOMAIN_STATUS_SORT_ORDER.length,
          );
          return bestRank(rowA) - bestRank(rowB);
        },
        meta: { filterVariant: 'select', filterOptions: SENDGRID_DOMAIN_STATUSES },
        cell: (info) => {
          const record = info.row.original;
          if (!record.ff_record) return null;

          const domains = record.ff_record.sendgrid_domains || [];

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {domains.map((sendgridDomain) => (
                <div key={sendgridDomain.id} className="rounded p-1" style={{ display: 'flex', alignItems: 'center' }}>
                  <DomainStatusBadge status={sendgridDomain.status} />
                </div>
              ))}
            </div>
          );
        },
      },
      {
        id: 'sendgrid_domains',
        header: 'Sending Domains',
        accessorFn: (row) => (row.ff_record?.sendgrid_domains || []).map((sd) => sd.domain).join(' '),
        cell: (info) => {
          const record = info.row.original;
          if (!record.ff_record) return null;

          const divisionId = record.ff_record.id;
          const domains = record.ff_record.sendgrid_domains || [];
          const isAddOpen = addDomainOpenIds.has(divisionId);
          const draft = addDomainDrafts[divisionId] || '';
          const addKey = `${divisionId}-add_domain`;
          const isAdding = pendingKeys.has(addKey);

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '220px' }}>
              {domains.map((sendgridDomain, index) => {
                const isLast = index === domains.length - 1;
                return (
                  <div key={sendgridDomain.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div
                      className="border rounded p-1"
                      style={{ cursor: 'pointer', flex: 1 }}
                      onClick={() => setDomainDetailsModal({ divisionId, sendgridDomainId: sendgridDomain.id })}
                    >
                      {sendgridDomain.domain}
                    </div>
                    {isLast && !isAddOpen && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        style={{ flexShrink: 0 }}
                        title="Add Domain"
                        onClick={() => setAddDomainOpenIds((prev) => new Set(prev).add(divisionId))}
                      >
                        +
                      </button>
                    )}
                  </div>
                );
              })}

              {domains.length === 0 && !isAddOpen && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  style={{ alignSelf: 'flex-end' }}
                  title="Add Domain"
                  onClick={() => setAddDomainOpenIds((prev) => new Set(prev).add(divisionId))}
                >
                  +
                </button>
              )}

              {isAddOpen ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="example.com"
                    value={draft}
                    onChange={(e) => setAddDomainDrafts((prev) => ({ ...prev, [divisionId]: e.target.value }))}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={isAdding || !draft.trim()}
                      onClick={() => addSendgridDomain(record)}
                    >
                      {isAdding ? 'Adding...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => setAddDomainOpenIds((prev) => {
                        const next = new Set(prev);
                        next.delete(divisionId);
                        return next;
                      })}
                    >
                      Cancel
                    </button>
                  </div>
                  {rowErrors[addKey] && <div className="text-danger" style={{ fontSize: '11px' }}>{rowErrors[addKey]}</div>}
                </div>
              ) : null}
            </div>
          );
        },
      },
    ],
    [pendingKeys, rowErrors, addDomainOpenIds, addDomainDrafts, syncKeyDrafts, hubspotData, syncPreview.isOpen],
  )

  const modalRecord = domainDetailsModal
    ? combinedData.find((r) => r.ff_record?.id === domainDetailsModal.divisionId)
    : null;
  const modalDomain = modalRecord
    ? (modalRecord.ff_record.sendgrid_domains || []).find((d) => d.id === domainDetailsModal.sendgridDomainId)
    : null;

  return (
    <>
    <div className="border bg-white p-3">
      <div className="border" style={{ flexDirection: 'row', display: 'flex' }}>
        <div style={{ padding: '10px', width: '50%' }}>
          <strong>Sync Status:</strong> <span className="text-muted" style={{ fontSize: '12px' }}>(click a status to filter)</span>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('synced')}
            title="Filter to synced records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: 'white', borderRadius: '3px' }}>{statusCounts.synced || 0}</b>
            <b className='border px-1' style={{ backgroundColor: 'white', borderRadius: '3px' }}>synced</b> Record exists in both systems and is actively syncing leads
          </div>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('ff_disabled_synced')}
            title="Filter to ff_disabled_synced records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>{statusCounts.ff_disabled_synced || 0}</b>
            <b className='border px-1' style={{ backgroundColor: '#f5f5f5', borderRadius: '3px' }}>ff_disabled_synced</b> Record exists in both systems but do not fetch leads since this record is disabled in Fastlane
          </div>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('ff_only')}
            title="Filter to ff_only records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.ff_only || 0}</b>
            <b className='border px-1' style={{ backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>ff_only</b> Record exists in Fastlane but not in Hubspot
          </div>
          <div
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
            onClick={() => toggleSyncStatusFilter('hs_only')}
            title="Filter to hs_only records"
          >
            <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.hs_only || 0}</b>
            <b className='border px-1' style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>hs_only</b> Record exists in Hubspot but not in Fastlane
          </div>
        </div>
        <div style={{ padding: '10px', width: '50%' }}>
          <strong>Key Fields:</strong>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px' }}>
            <b className='border px-1' style={{ borderRadius: '3px', height: '20px' }}>Sync Key</b>
            <ul>
              <li><b>hubspot_name</b> in FF must match the <b>value</b> field in Hubspot for the record to be considered a match.</li>
              <li>If there is a record in FF with a hubspot_name that does not match any value in Hubspot, it will be marked as "ff_only".</li>
              <li>If there is a record in Hubspot with a value that does not match any hubspot_name in FF, it will be marked as "hs_only".</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px' }}>
            <b className='border px-1' style={{ borderRadius: '3px', height: '20px' }}>FF Disabled At</b>
            <ul>
              <li>This field in FF indicates whether the record is disabled and has a <strong>disabled_at</strong> timestamp.</li>
              <li>If it has a timestamp, it means the record is disabled and will not fetch leads, even if it exists in Hubspot.</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Suggestions for the Sync Key editor — it has to match a HubSpot brand value exactly. */}
      <datalist id="divisions-hubspot-values">
        {hubspotData.map((hs) => <option key={hs.value} value={hs.value} />)}
      </datalist>
      <Table
        loadingFFData={loadingFFData}
        loadingHubspotData={loadingHubspotData}
        data={combinedData}
        columns={columns}
        initialSorting={[
          { id: 'sync_status', desc: false },
          { id: 'brand_name', desc: false },
        ]}
        renderToolbar={(table) => {
          tableInstanceRef.current = table;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={verifyAllDomainsState.isRunning}
                onClick={verifyAllDomains}
                title="Rechecks every sending domain on this side against SendGrid's DNS validation"
              >
                {verifyAllDomainsState.isRunning ? 'Verifying Domains...' : 'Verify All Domains'}
              </button>
              {verifyAllDomainsState.message && (
                <span className={verifyAllDomainsState.isError ? 'text-danger' : 'text-muted'} style={{ fontSize: '12px' }}>
                  {verifyAllDomainsState.message}
                </span>
              )}
            </div>
          );
        }}
      />
    </div>

    {syncPreview.isOpen && (
      <>
        <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Division From HubSpot</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeSyncPreview}
                  aria-label="Close"
                  disabled={syncPreview.isSaving}
                />
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {syncPreview.isLoading && <div className="text-muted">Loading preview...</div>}

                {syncPreview.error && (
                  <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-line' }}>
                    {syncPreview.error}
                  </div>
                )}

                {!syncPreview.isLoading && syncPreview.fields && (
                  <>
                    <p className="text-muted" style={{ fontSize: '13px' }}>
                      This creates a new division for the HubSpot brand <b>{syncPreview.record?.hs_record?.value}</b> with
                      the values below — the same way new brands are created automatically when HubSpot sends one over.
                      It starts hidden (carpool_active false); use the row toggles to change that afterwards.
                    </p>
                    <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Property</th>
                          <th>Old Value</th>
                          <th>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syncPreview.fields.map(({ property, from, to, changed }) => (
                          <tr key={property}>
                            <td>{humanizeDiffKey(property)}</td>
                            <td>{formatDiffValue(from)}</td>
                            <td style={changed ? { backgroundColor: 'rgba(40, 167, 69, 0.2)', fontWeight: 600 } : undefined}>
                              {formatDiffValue(to)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeSyncPreview}
                  disabled={syncPreview.isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmSyncPreview}
                  disabled={syncPreview.isLoading || syncPreview.isSaving || !syncPreview.fields}
                >
                  {syncPreview.isSaving ? 'Creating...' : 'Create Division'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show" onClick={syncPreview.isSaving ? undefined : closeSyncPreview} />
      </>
    )}

    {confirmState && (
      <>
        <div className="modal show" style={{ display: 'block', zIndex: 1060 }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{confirmState.title}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeConfirmation}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>{confirmState.message}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeConfirmation}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const { onConfirm } = confirmState;
                    closeConfirmation();
                    onConfirm();
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show" style={{ zIndex: 1055 }} onClick={closeConfirmation} />
      </>
    )}

    {domainDetailsModal && modalRecord && modalDomain && (() => {
      const divisionId = modalRecord.ff_record.id;
      const recheckKey = `${divisionId}-recheck-${modalDomain.id}`;
      const removeKey = `${divisionId}-remove-${modalDomain.id}`;
      const isRechecking = pendingKeys.has(recheckKey);
      const isRemoving = pendingKeys.has(removeKey);
      const dnsRecords = modalDomain.dns_records || {};
      const otherDivisionsSharingDomain = ffData.filter((d) => (
        d.id !== divisionId && (d.sendgrid_domains || []).some((sd) => sd.id === modalDomain.id)
      ));

      return (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {modalDomain.domain}
                    <DomainStatusBadge status={modalDomain.status} />
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setDomainDetailsModal(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  {otherDivisionsSharingDomain.length > 0 && (
                    <div className="alert alert-warning">
                      <strong>Shared domain:</strong> also used by{' '}
                      {otherDivisionsSharingDomain.map((d) => d.name).join(', ')}
                    </div>
                  )}
                  {Object.keys(dnsRecords).length > 0 ? (
                    <>
                      <p className="text-muted">Here are the exact records to add (additive changes only):</p>
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Host/Name</th>
                            <th>Value/Points to</th>
                            <th>Valid</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.values(dnsRecords).map((dnsRecord, idx) => (
                            <tr key={idx}>
                              <td>CNAME</td>
                              <td>{dnsRecord.host}</td>
                              <td>{dnsRecord.data}</td>
                              <td>
                                <Badge
                                  tone={dnsRecord.valid === undefined ? 'neutral' : (dnsRecord.valid ? 'success' : 'error')}
                                  title={dnsRecord.reason || ''}
                                >
                                  {dnsRecord.valid === undefined ? 'unknown' : (dnsRecord.valid ? 'valid' : 'invalid')}
                                </Badge>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  style={{ backgroundColor: 'var(--primary)', color: '#fff' }}
                                  onClick={() => navigator.clipboard?.writeText(`${dnsRecord.host} CNAME ${dnsRecord.data}`)}
                                >
                                  Copy
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p className="text-muted" style={{ marginBottom: 0 }}>No DNS records yet. Try rechecking this domain.</p>
                  )}
                  {rowErrors[recheckKey] && <div className="text-danger" style={{ fontSize: '12px' }}>{rowErrors[recheckKey]}</div>}
                  {rowErrors[removeKey] && <div className="text-danger" style={{ fontSize: '12px' }}>{rowErrors[removeKey]}</div>}
                </div>
                <div className="modal-footer">
                  {Object.keys(dnsRecords).length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ backgroundColor: 'var(--primary)', color: '#fff' }}
                      onClick={() => copyAllDnsRecords(modalDomain)}
                    >
                      Copy Records
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => recheckSendgridDomain(modalRecord, modalDomain.id)}
                    disabled={isRechecking}
                  >
                    {isRechecking ? 'Checking...' : 'Recheck'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeSendgridDomain(modalRecord, modalDomain, () => setDomainDetailsModal(null))}
                    disabled={isRemoving}
                  >
                    Remove
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setDomainDetailsModal(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setDomainDetailsModal(null)} />
        </>
      );
    })()}
    </>
  );
};

const element = document.getElementById('divisions-table');
if (element) {
  const root = createRoot(element);
  root.render(<DivisionsTable/>);
}
